import "core-js/modules/es.symbol.js";
import "core-js/modules/es.symbol.description.js";
import "core-js/modules/es.array.concat.js";
import "core-js/modules/es.function.name.js";
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import getLocalisedDate from 'utils/dates/getLocalisedDate';
import shouldShowJoinMeetingLink from 'utils/shouldShowJoinMeetingLink';
import getUsersISOLocale from 'utils/i18n/getUsersISOLocale';
import { DIMENSIONS } from 'utils/image/getPictureUrl';
import config from 'utils/config';
import Box from 'components/Box';
import Button from 'components/Button';
import Stack from 'components/Stack';
import Text from 'components/Text';
import UserGeneratedText from 'components/UserGeneratedText';
import CardImage from 'components/CardImage';
import Icon from 'components/Icon';
import Heading from 'components/Heading';
import { EventStatusPill } from 'components/Pill';
import Flex from 'components/Flex';
import CenterContent from 'components/CenterContent';
import Card from 'components/Card';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
function getEventTimeLabel(_ref) {
  var startDateTime = _ref.startDateTime,
    endDateTime = _ref.endDateTime;
  var startDate = new Date(startDateTime);
  var endDate = new Date(endDateTime);
  if (!isFinite(startDate.getTime())) {
    return null;
  }
  var formatter = new Intl.DateTimeFormat(getUsersISOLocale(), {
    hour: 'numeric',
    minute: '2-digit'
  });
  if (!isFinite(endDate.getTime())) {
    return formatter.format(startDate);
  }
  if (typeof formatter.formatRange === 'function') {
    return formatter.formatRange(startDate, endDate);
  }

  // Fallback for browsers that don't support Intl.DateTimeFormat.formatRange
  var startTime = formatter.format(startDate);
  var endTime = formatter.format(endDate);
  return "".concat(startTime, " - ").concat(endTime);
}
function EventAssetContent(_ref2) {
  var _ref2$isSimpleViewerC = _ref2.isSimpleViewerContext,
    isSimpleViewerContext = _ref2$isSimpleViewerC === void 0 ? false : _ref2$isSimpleViewerC,
    asset = _ref2.asset,
    onLoad = _ref2.onLoad;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;

  // Event assets use CardImage, which doesn't expose load/error handlers,
  // so we mark content as ready once this view mounts.
  useEffect(function () {
    onLoad();
  }, [onLoad]);
  if (asset.type !== 'event') {
    return null;
  }
  var eventSession = asset.eventSession;
  var eventStatus = eventSession === null || eventSession === void 0 ? void 0 : eventSession.status;
  var isEventAssetsEnabled = Boolean(config.eventAssets);
  var eventDate = eventSession !== null && eventSession !== void 0 && eventSession.startDateTime ? getLocalisedDate({
    date: eventSession.startDateTime,
    options: {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }
  }) : null;
  var eventTime = eventSession !== null && eventSession !== void 0 && eventSession.startDateTime ? getEventTimeLabel({
    startDateTime: eventSession.startDateTime,
    endDateTime: eventSession.endDateTime
  }) : null;
  var pictureUrl = asset.pictureUrl ? "".concat(asset.pictureUrl, "/").concat(DIMENSIONS.PLAYLIST_HERO) : null;
  var joiningInstructions = (eventSession === null || eventSession === void 0 ? void 0 : eventSession.joiningInstructions) || asset.joiningInstructions;
  var joinMeetingLink = eventSession === null || eventSession === void 0 ? void 0 : eventSession.linkUrl;
  var isNotBooked = !eventSession;
  var isBooked = eventStatus === 'booked';
  var isAttended = eventStatus === 'attended';
  var isCancelledOrNoShow = eventStatus === 'cancelled' || eventStatus === 'noshow';
  var showUserEventActions = isEventAssetsEnabled && !isSimpleViewerContext;
  var showEventDetails = isSimpleViewerContext ? false : isBooked || isAttended || isCancelledOrNoShow;
  var showBookingInstructions = (isSimpleViewerContext || isNotBooked || isCancelledOrNoShow) && Boolean(asset.bookingInstructions) && isEventAssetsEnabled;
  var showJoinMeetingButton = showUserEventActions && shouldShowJoinMeetingLink({
    status: eventStatus,
    startDateTime: eventSession === null || eventSession === void 0 ? void 0 : eventSession.startDateTime,
    endDateTime: eventSession === null || eventSession === void 0 ? void 0 : eventSession.endDateTime,
    linkUrl: joinMeetingLink
  });
  var showJoiningInstructions = isEventAssetsEnabled && (isSimpleViewerContext || isBooked) && Boolean(joiningInstructions);
  return /*#__PURE__*/_jsx(Card, {
    width: "100%",
    minHeight: "100%",
    children: /*#__PURE__*/_jsxs(Stack, {
      spacing: "m",
      children: [/*#__PURE__*/_jsx(CardImage, {
        src: pictureUrl,
        alt: asset.alternativeText,
        colorBlock: "asset-event",
        aspectRatio: 341 / 1364,
        fallbackContent: /*#__PURE__*/_jsx(Icon, {
          dimmed: true,
          name: "asset-event",
          scale: 1.5
        })
      }), /*#__PURE__*/_jsxs(Stack, {
        spacing: "m",
        p: "m",
        children: [showUserEventActions && /*#__PURE__*/_jsx(Box, {
          children: /*#__PURE__*/_jsx(EventStatusPill, {
            status: eventStatus
          })
        }), /*#__PURE__*/_jsxs(Stack, {
          spacing: "s",
          children: [asset.name && /*#__PURE__*/_jsx(Heading, {
            as: "h2",
            bold: true,
            size: "large",
            children: asset.name
          }), asset.description && /*#__PURE__*/_jsx(UserGeneratedText, {
            text: asset.description
          })]
        }), showEventDetails && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(Heading, {
            as: "h3",
            bold: true,
            size: "medium",
            mb: "s",
            children: t('property_event_details')
          }), /*#__PURE__*/_jsxs(Stack, {
            spacing: "xs",
            children: [/*#__PURE__*/_jsxs(Flex, {
              gap: "xs",
              children: [/*#__PURE__*/_jsx(Text, {
                bold: true,
                children: t('property_date_colon')
              }), /*#__PURE__*/_jsx(Text, {
                children: eventDate
              })]
            }), /*#__PURE__*/_jsxs(Flex, {
              gap: "xs",
              children: [/*#__PURE__*/_jsx(Text, {
                bold: true,
                children: t('property_time_colon')
              }), /*#__PURE__*/_jsx(Text, {
                children: eventTime
              })]
            }), (eventSession === null || eventSession === void 0 ? void 0 : eventSession.location) && /*#__PURE__*/_jsxs(Flex, {
              gap: "xs",
              children: [/*#__PURE__*/_jsx(Text, {
                bold: true,
                children: t('property_location_colon')
              }), /*#__PURE__*/_jsx(Text, {
                children: eventSession.location
              })]
            })]
          })]
        }), showBookingInstructions && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(Heading, {
            as: "h3",
            bold: true,
            size: "medium",
            mb: "s",
            children: t('form_booking_instructions')
          }), /*#__PURE__*/_jsx(UserGeneratedText, {
            text: asset.bookingInstructions || ''
          })]
        }), showJoiningInstructions && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(Heading, {
            as: "h3",
            bold: true,
            size: "medium",
            mb: "s",
            children: t('form_joining_instructions')
          }), /*#__PURE__*/_jsx(UserGeneratedText, {
            text: joiningInstructions || ''
          })]
        }), showJoinMeetingButton && joinMeetingLink && /*#__PURE__*/_jsx(CenterContent, {
          children: /*#__PURE__*/_jsxs(Box, {
            textAlign: "center",
            children: [/*#__PURE__*/_jsx(Button, {
              as: "a",
              href: joinMeetingLink || '',
              target: "_blank",
              rel: "noopener noreferrer",
              icon: "external",
              variant: "primary",
              children: t('actions_join_meeting')
            }), /*#__PURE__*/_jsx(Text, {
              dimmed: true,
              as: "p",
              size: "s",
              mt: "xs",
              children: t('external_link_opens_in_new_tab')
            })]
          })
        })]
      })]
    })
  });
}
export default EventAssetContent;